import { Activity, Folders, GitBranch, Layers, Sigma } from "lucide-react";
import {
  ANALYSIS_TYPE_CROSS,
  ANALYSIS_TYPE_VARIANT,
  COUNT_METRIC,
} from "../constants";
import ABCXYZInfoTooltip from "./ABCXYZInfoTooltip";

/** Compact number formatting for KPI values (de-DE grouping like other tiles). */
const formatNumber = (value, decimals = 0) => {
  if (value === null || value === undefined) return "—";
  return Number(value).toLocaleString("de-DE", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

const AVG_CV_LABEL_BADGE_CLASSES = {
  Stable: "bg-green-50 text-green-700",
  Varying: "bg-amber-50 text-amber-700",
  Sporadic: "bg-red-50 text-red-700",
};

const KpiCard = ({
  label,
  value,
  icon: Icon,
  iconClass,
  info = null,
  badge = null,
}) => (
  <div className="flex-1 basis-0 min-w-[150px] min-h-[52px] bg-white border border-gray-200 rounded-lg px-3 py-1.5 flex items-center gap-2">
    <div
      className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${iconClass}`}
    >
      <Icon className="w-4 h-4" />
    </div>
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-1">
        <span className="text-xs font-medium text-gray-500 truncate">
          {label}
        </span>
        {info}
      </div>
      <div className="text-lg font-semibold text-gray-900 leading-tight">
        {value}
      </div>
    </div>
    {badge}
  </div>
);

/**
 * KPI cards for the ABC/XYZ dashboard — icon-chip layout. The component
 * returns a fragment; the parent owns the auto-fit grid that keeps the cards
 * on one responsive row whatever the mode's card count.
 *
 * Variant mode: Total Variants, Total Cases, Avg. Variability. Object mode:
 * Total Object Groups, Total Objects, Total Metric Value (numeric metrics
 * only), Avg. Variability. Cross mode joins the two, so its row is the union
 * of their scope cards: Total Cases, Total Variants, Total Object Groups.
 */
const ABCXYZKpiCards = ({
  mode,
  summary = null,
  abcMetricName = COUNT_METRIC,
}) => {
  // Rendered before any result exists too: Object and Cross only fetch once
  // their configuration is applied, and hiding the row until then would both
  // reflow the header and leave the user guessing what the mode reports.
  // Missing values read as an em dash, the placeholder used across the tiles.
  const variabilityValue =
    summary?.avg_cv === null || summary?.avg_cv === undefined
      ? "—"
      : `${formatNumber(summary?.avg_cv, 1)} %`;
  const variabilityInfo = (
    <ABCXYZInfoTooltip sections={["cv"]} title="Avg. Variability" />
  );
  const variabilityBadge = summary?.avg_cv_label ? (
    <span
      className={`px-2.5 py-1 rounded-full text-sm font-semibold shrink-0 ${
        AVG_CV_LABEL_BADGE_CLASSES[summary.avg_cv_label] ||
        "bg-gray-100 text-gray-500"
      }`}
    >
      {summary.avg_cv_label}
    </span>
  ) : null;

  if (mode === ANALYSIS_TYPE_CROSS) {
    const combinationsInfo = summary?.object_type ? (
      <ABCXYZInfoTooltip
        title="Total Combinations"
        sections={[]}
        extraLines={[
          {
            label: formatNumber(summary?.total_combinations),
            text:
              `unique "${summary.object_type}" × variant combinations in ` +
              `this cross analysis (${formatNumber(summary?.total_underlying_objects)} ` +
              `underlying "${summary.object_type}" objects across them).`,
          },
        ]}
      />
    ) : null;
    // Scope of the two joined analyses plus a data-quality count — the same
    // shape as the other modes' rows. The decision counts deliberately do NOT
    // appear here: they are the AA/AC/CA cells of the matrix right below, so a
    // card would only repeat them, and the cells name their own decision.
    return (
      <>
        <KpiCard
          label="Total Combinations"
          value={formatNumber(summary?.total_combinations)}
          icon={Layers}
          iconClass="bg-gray-100 text-primary-600"
          info={combinationsInfo}
        />
        <KpiCard
          label="Total Variants"
          value={formatNumber(summary?.total_variants)}
          icon={GitBranch}
          iconClass="bg-gray-100 text-primary-600"
        />
        <KpiCard
          label="Total Object Groups"
          value={formatNumber(summary?.total_object_groups)}
          icon={Folders}
          iconClass="bg-gray-100 text-primary-600"
        />
      </>
    );
  }

  if (mode === ANALYSIS_TYPE_VARIANT) {
    return (
      <>
        <KpiCard
          label="Total Variants"
          value={formatNumber(summary?.total_variants)}
          icon={GitBranch}
          iconClass="bg-gray-100 text-primary-600"
        />
        <KpiCard
          label="Total Cases"
          value={formatNumber(summary?.total_cases)}
          icon={Layers}
          iconClass="bg-gray-100 text-primary-600"
        />
        <KpiCard
          label="Avg. Variability"
          value={variabilityValue}
          icon={Activity}
          iconClass="bg-gray-100 text-primary-600"
          info={variabilityInfo}
          badge={variabilityBadge}
        />
      </>
    );
  }

  return (
    <>
      <KpiCard
        label="Total Object Groups"
        value={formatNumber(summary?.total_object_groups)}
        icon={Folders}
        iconClass="bg-gray-100 text-primary-600"
      />
      <KpiCard
        label="Total Objects"
        value={formatNumber(summary?.total_objects)}
        icon={Layers}
        iconClass="bg-gray-100 text-primary-600"
      />
      {abcMetricName !== COUNT_METRIC && (
        <KpiCard
          label="Total Metric Value"
          value={formatNumber(summary?.total_metric_value, 2)}
          icon={Sigma}
          iconClass="bg-gray-100 text-primary-600"
          info={
            <ABCXYZInfoTooltip
              sections={["totalMetricValue"]}
              title="Total Metric Value"
            />
          }
        />
      )}
      <KpiCard
        label="Avg. Variability"
        value={variabilityValue}
        icon={Activity}
        iconClass="bg-gray-100 text-primary-600"
        info={variabilityInfo}
        badge={variabilityBadge}
      />
    </>
  );
};

export default ABCXYZKpiCards;
